/**
 * Escape-to-close, focus in on open, focus back on close (BON-472).
 *
 * Lifted to a shared hook because this slice adds three dialogs and the
 * return-focus half is the part that is easy to leave out: without it a keyboard
 * user who tabbed a long way down the reservations table is dropped back at the
 * top of the document every time a dialog closes.
 *
 * `ReleaseReservationDialog` still carries its own private copy. Migrating it
 * would mean splitting that file for `react/no-multi-comp` — a refactor with no
 * behavioural change, which this slice has no business carrying.
 */
import { useEffect } from 'react';

export function useDialogKeyboard(
  onClose: () => void,
  focusOnOpen: React.RefObject<HTMLElement>,
  /**
   * Ignore Escape while this is true — for a request already in flight.
   *
   * NOTHING CAN CALL THE REQUEST BACK once it has left, so an Escape that
   * dismissed the dialog would not cancel it; it would only hide the answer.
   * A CSR who believes they stopped a resend, and then watches a success
   * message appear, has already told the customer the opposite.
   */
  disabled = false,
): void {
  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    focusOnOpen.current?.focus();

    return () => {
      returnTo?.focus();
    };
    // `focusOnOpen` is a stable ref from the caller; re-running on its identity
    // would re-steal focus mid-edit. Focus is claimed once per mount and given
    // back once per unmount — deliberately not re-run when `disabled` flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, []);

  useEffect(() => {
    if (disabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, disabled]);
}
